// Scroll-reveal: content slides up + fades in as it enters the viewport.
//
// Design notes:
// - Classes are added by JS only, so with JS off / reduced-motion the page is
//   fully visible (see _animations.scss).
// - Elements already on-screen at load are left untouched (no load flicker);
//   only below-the-fold content animates on scroll.
// - Card/row grids stagger their children; other blocks reveal as a whole.
const SKIP = '.ax-header, .ax-footer, .ax-page-hero';
const GRIDS = '[class*="__grid"], .single-blog__related-grid, .awsm-job-listings';

const ScrollReveal = {
	OnReady() {
		if (!('IntersectionObserver' in window)) {
			return;
		}
		if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
			return;
		}
		// Wait for full load so image-driven layout heights are settled before
		// we decide what is above vs. below the fold.
		if (document.readyState === 'complete') {
			ScrollReveal.init();
		} else {
			window.addEventListener('load', ScrollReveal.init, { once: true });
		}
	},

	init() {
		const viewportH = window.innerHeight || document.documentElement.clientHeight;
		const targets = [];

		const mark = (el, index) => {
			if (!el || el.nodeType !== 1 || el.classList.contains('ax-reveal')) {
				return;
			}
			// Leave content that is already (nearly) in view visible — only
			// animate what the user will actually scroll down to.
			if (el.getBoundingClientRect().top < viewportH * 0.85) {
				return;
			}
			el.classList.add('ax-reveal');
			const delay = Math.min(index, 6) * 90;
			if (delay) {
				el.style.transitionDelay = delay + 'ms';
			}
			targets.push(el);
		};

		// 1) Staggered reveal for the items inside card/row grids.
		document.querySelectorAll(GRIDS).forEach((grid) => {
			const kids = Array.prototype.filter.call(grid.children, (node) => node.nodeType === 1);
			if (kids.length >= 2) {
				kids.forEach((kid, i) => mark(kid, i));
			}
		});

		// 2) Whole-section reveal for the remaining content blocks.
		document.querySelectorAll('.creative-theme-block').forEach((section) => {
			if (section.matches(SKIP) || section.closest(SKIP)) {
				return;
			}
			if (section.querySelector(GRIDS)) {
				return; // its items were already handled above
			}
			mark(section, 0);
		});

		if (!targets.length) {
			return;
		}

		const observer = new IntersectionObserver(
			(entries, obs) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						entry.target.classList.add('is-visible');
						obs.unobserve(entry.target);
					}
				});
			},
			{ threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
		);

		targets.forEach((el) => observer.observe(el));
	},
};

export default ScrollReveal;
